/**
 * Created by chachora on 11/28/18.
 */

import { ThunkAction } from 'redux-thunk';
import { AnyAction } from 'redux';
import { t } from 'i18next';
import * as stApi from '../api/station-tools-api';
import * as uralReport from '../reports/ural-report';
import * as selectors from './selectors';
import * as utils from '../helpers/utils';
import * as eventHelper from '../helpers/event-helper';
import { RootState } from './reducers';
import { SlotEvent } from '../model/types';
import { NOTIFICATION_SHOW } from './notification.actions';

export const EVENTS_GET = 'EVENTS_GET';
export const EVENT_UPDATE = 'EVENT_UPDATE';
export const EVENT_DELETE = 'EVENT_DELETE';

let getEventsAbortController: AbortController | null = null;

export const getEvents =
  (range?: {
    start: Date;
    end: Date;
  }): ThunkAction<Promise<void>, RootState, unknown, AnyAction> =>
  async (dispatch, getState) => {
    const curRange = range || selectors.getRange(getState());
    if (!curRange || (getEventsAbortController != null && !range)) {
      // Skip if no range, or if a request is already in-flight and this is a
      // polling call (no explicit range) — avoids overlapping with user navigation
      return;
    }

    getEventsAbortController?.abort();
    getEventsAbortController = new AbortController();

    try {
      let events = await stApi.getEvents(
        {
          start: curRange.start.toUTCString(),
          end: curRange.end.toUTCString(),
        },
        { signal: getEventsAbortController.signal }
      );

      if (events == null) {
        return;
      }

      getEventsAbortController = undefined;
      events = events.map(event => eventHelper.refineEvent(event));
      dispatch({ type: EVENTS_GET, payload: { events, range: curRange } });
    } catch (err) {
      throw err;
    }
  };

type Messages = {
  successMessage?: string;
  errorMessage?: string;
};

const withMessages = async (
  dispatch: (action: AnyAction) => unknown,
  action: () => Promise<unknown>,
  successMessage: string,
  errorMessage: string
) => {
  try {
    await action();

    if (!successMessage) {
      return;
    }

    dispatch({
      type: NOTIFICATION_SHOW,
      payload: { message: successMessage, severity: 'success' },
    });
  } catch (error) {
    if (!errorMessage) {
      throw error;
    }

    dispatch({
      type: NOTIFICATION_SHOW,
      payload: { message: errorMessage, severity: 'error' },
    });
  }
};

export const updateEvent =
  (
    eventId: string,
    data: Partial<SlotEvent>,
    opts: Messages = {}
  ): ThunkAction<Promise<void>, RootState, unknown, AnyAction> =>
  async (dispatch, getState) => {
    const {
      successMessage = t('event_success_update'),
      errorMessage = t('event_error_update'),
    } = opts;

    await withMessages(
      dispatch,
      async () => {
        const event = selectors.getEventById(getState(), eventId);
        if (!event) {
          return;
        }
        const changedValues = utils.getFormikChangedValues(event, data);
        let newEvent = await stApi.updateEventV2(eventId, changedValues);
        dispatch(createUpdateEventAction(newEvent));
      },
      successMessage,
      errorMessage
    );
  };

export const deleteEvent =
  (
    eventId: string,
    opts: Messages = {}
  ): ThunkAction<Promise<void>, RootState, unknown, AnyAction> =>
  async dispatch => {
    const {
      successMessage = t('event_success_delete'),
      errorMessage = t('event_error_delete'),
    } = opts;

    await withMessages(
      dispatch,
      async () => {
        await stApi.deleteEvent(eventId);
        dispatch({ type: EVENT_DELETE, payload: { eventId } });
      },
      successMessage,
      errorMessage
    );
  };

export const submitEventReport =
  (
    eventId: string,
    opts: Messages = {}
  ): ThunkAction<Promise<void>, RootState, unknown, AnyAction> =>
  async (dispatch, getState) => {
    const {
      successMessage = t('event_success_report'),
      errorMessage = t('event_error_report'),
    } = opts;

    await withMessages(
      dispatch,
      async () => {
        const currEvent = selectors.getEventsById(getState()).get(eventId);
        const report = uralReport.getUralReportData(currEvent);
        let event = await stApi.submitEventReport(eventId, report);
        dispatch(createUpdateEventAction(event));
      },
      successMessage,
      errorMessage
    );
  };

/*
  Action creators
 */
const createUpdateEventAction = (event: SlotEvent) => {
  event = eventHelper.refineEvent(event);
  return { type: EVENT_UPDATE, payload: { event } };
};
